import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as XLSX from 'xlsx';
import './TtsCall.css';

interface CallHistory {
  id: string;
  phoneNumbers: string;
  timestamp: string;
  count: number;
}

interface SurveyAnswerDto {
  questionId: number;
  questionText: string;
  answer: string;
  recordedAt: string;
}

interface FullCallHistory {
  id: number;
  companyId: number;
  phoneNumber: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  serviceType: string;
  sentAt: string;
  cost: number;
  errorMessage?: string;
  surveyTitle?: string;
  surveyAnswers?: SurveyAnswerDto[];
  eventCount: number;
}

interface MessageTemplate {
  id: string;
  message: string;
  timestamp: string;
}

interface CallResult {
  id: string;
  phoneNumber: string;
  status: 'pending' | 'calling' | 'success' | 'failed';
  message: string;
  timestamp: string;
}

const COST_PER_CALL = 30; // 통화당 30원

const TtsCall: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateCreditBalance, refreshCreditBalance } = useAuth();
  const [formData, setFormData] = useState({
    phoneNumbers: '',
    message: ''
  });

  const [callHistory, setCallHistory] = useState<CallHistory[]>([]);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [callResults, setCallResults] = useState<CallResult[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<string[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showFullHistoryModal, setShowFullHistoryModal] = useState(false);
  const [fullCallHistory, setFullCallHistory] = useState<FullCallHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // 필터 상태
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterServiceType, setFilterServiceType] = useState<string>('');
  const [filterPhoneNumber, setFilterPhoneNumber] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // 파일 업로드 관련 상태
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileRows, setFileRows] = useState<string[][]>([]);
  const [selectedColumn, setSelectedColumn] = useState<number>(-1);

  useEffect(() => {
    loadCallHistory();
    loadMessageTemplates();
  }, []);

  const loadCallHistory = () => {
    // TODO: API 호출로 대체
    const savedHistory = localStorage.getItem('callHistory');
    if (savedHistory) {
      setCallHistory(JSON.parse(savedHistory));
    }
  };

  const loadMessageTemplates = () => {
    // TODO: API 호출로 대체
    const savedTemplates = localStorage.getItem('messageTemplates');
    if (savedTemplates) {
      setMessageTemplates(JSON.parse(savedTemplates));
    }
  };

  const saveToHistory = (phoneNumbers: string) => {
    const newHistory: CallHistory = {
      id: Date.now().toString(),
      phoneNumbers,
      timestamp: new Date().toISOString(),
      count: phoneNumbers.split('|').length
    };
    const updated = [newHistory, ...callHistory].slice(0, 20);
    setCallHistory(updated);
    localStorage.setItem('callHistory', JSON.stringify(updated));
  };

  const saveMessageTemplate = (message: string) => {
    const exists = messageTemplates.some(t => t.message === message);
    if (!exists) {
      const newTemplate: MessageTemplate = {
        id: Date.now().toString(),
        message,
        timestamp: new Date().toISOString()
      };
      const updated = [newTemplate, ...messageTemplates].slice(0, 10);
      setMessageTemplates(updated);
      localStorage.setItem('messageTemplates', JSON.stringify(updated));
    }
  };

  // 구분자 자동 감지
  const detectDelimiter = (line: string): string => {
    const delimiters = [',', '\t', ';', '|'];
    let maxCount = 0;
    let bestDelimiter = ',';

    for (const delimiter of delimiters) {
      const count = line.split(delimiter).length;
      if (count > maxCount) {
        maxCount = count;
        bestDelimiter = delimiter;
      }
    }

    return maxCount > 1 ? bestDelimiter : ',';
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (isExcel) {
      // 엑셀 파일 처리
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });

          // 첫 번째 시트 가져오기
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // 시트를 JSON으로 변환 (헤더 포함)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          if (jsonData.length === 0) {
            alert('파일이 비어있습니다.');
            return;
          }

          // 첫 줄을 헤더로, 나머지를 데이터로 분리
          const headers = jsonData[0].map((h: any) => String(h || '').trim());
          const rows = jsonData.slice(1).map(row =>
            row.map((cell: any) => String(cell || '').trim())
          );

          setFileHeaders(headers);
          setFileRows(rows);
          setSelectedColumn(-1);
          setShowColumnModal(true);
        } catch (error) {
          console.error('Excel parsing error:', error);
          alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
        }
      };

      reader.readAsArrayBuffer(file);
    } else {
      // CSV/TXT 파일 처리
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        const lines = content.split(/[\r\n]+/).filter(line => line.trim());

        if (lines.length === 0) {
          alert('파일이 비어있습니다.');
          return;
        }

        // 첫 줄로 구분자 감지
        const delimiter = detectDelimiter(lines[0]);

        // 첫 줄을 헤더로 파싱
        const headers = lines[0].split(delimiter).map(h => h.trim());

        // 나머지 줄들을 데이터로 파싱
        const rows = lines.slice(1).map(line =>
          line.split(delimiter).map(cell => cell.trim())
        );

        setFileHeaders(headers);
        setFileRows(rows);
        setSelectedColumn(-1);
        setShowColumnModal(true);
      };

      reader.readAsText(file);
    }

    // 파일 입력 초기화 (같은 파일 다시 선택 가능하도록)
    e.target.value = '';
  };

  // 컬럼 선택 후 전화번호 추가
  const handleColumnSelect = () => {
    if (selectedColumn === -1) {
      alert('컬럼을 선택해주세요.');
      return;
    }

    // 선택된 컬럼의 모든 값 추출
    const phoneNumbers = fileRows
      .map(row => row[selectedColumn])
      .filter(phone => phone && phone.trim())
      .map(phone => phone.trim());

    if (phoneNumbers.length === 0) {
      alert('선택한 컬럼에 유효한 데이터가 없습니다.');
      return;
    }

    // 기존 번호와 합치기
    const existing = formData.phoneNumbers ? formData.phoneNumbers.split('|') : [];
    const combined = [...existing, ...phoneNumbers].filter(num => num);

    setFormData({
      ...formData,
      phoneNumbers: combined.join('|')
    });

    // 모달 닫기
    setShowColumnModal(false);
    setFileHeaders([]);
    setFileRows([]);
    setSelectedColumn(-1);

    alert(`${phoneNumbers.length}개의 전화번호가 추가되었습니다.`);
  };

  const handleHistorySelect = (history: CallHistory) => {
    const phones = history.phoneNumbers.split('|');
    const currentSelected = new Set(selectedHistory);

    phones.forEach(phone => {
      if (currentSelected.has(phone)) {
        currentSelected.delete(phone);
      } else {
        currentSelected.add(phone);
      }
    });

    setSelectedHistory(Array.from(currentSelected));
  };

  const addSelectedToForm = () => {
    const existing = formData.phoneNumbers ? formData.phoneNumbers.split('|') : [];
    const allPhones = [...existing, ...selectedHistory];
    const combined = Array.from(new Set(allPhones));
    setFormData({
      ...formData,
      phoneNumbers: combined.join('|')
    });
    setSelectedHistory([]);
    setShowHistoryModal(false);
  };

  const handleTemplateSelect = (template: MessageTemplate) => {
    setFormData({
      ...formData,
      message: template.message
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      alert('로그인이 필요합니다.');
      return;
    }

    // 메시지 길이 검증 (60자 제한)
    if (formData.message.length > 60) {
      alert(`메시지가 너무 깁니다.\n현재: ${formData.message.length}자\n최대: 60자\n\nTTS 음성 메시지는 60자 이내로 작성해주세요.`);
      return;
    }

    // 전화번호 구분자 통일: ','를 '|'로 변경
    const normalizedPhoneNumbers = formData.phoneNumbers.replace(/,/g, '|');

    // 전화번호에서 '-' 제거
    const phones = normalizedPhoneNumbers
      .split('|')
      .map(p => p.trim().replace(/-/g, ''))
      .filter(p => p);

    if (phones.length === 0) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    const totalCost = phones.length * COST_PER_CALL;

    // 크레딧 잔액 확인
    if ((user.creditBalance || 0) < totalCost) {
      alert(`크레딧이 부족합니다.\n필요: ${totalCost.toLocaleString()}원\n보유: ${(user.creditBalance || 0).toLocaleString()}원`);
      return;
    }

    // 발송 내역 및 메시지 템플릿 저장
    saveToHistory(formData.phoneNumbers);
    saveMessageTemplate(formData.message);

    // 실시간 결과 초기화
    const results: CallResult[] = phones.map(phone => ({
      id: `${Date.now()}-${phone}`,
      phoneNumber: phone,
      status: 'pending',
      message: formData.message,
      timestamp: new Date().toISOString()
    }));
    setCallResults(results);

    try {
      // 실제 API 호출
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      setCallResults(prev => prev.map(r => ({ ...r, status: 'calling' })));

      const requestPayload = {
        companyId: user.companyId,
        companyName: user.companyName,
        phoneNumbers: phones.join('|'),
        message: formData.message,
        eventFlag: 1
      };

      console.log('[FRONTEND] TTS Request Payload:', requestPayload);
      console.log('[FRONTEND] Message encoding test:', {
        message: formData.message,
        length: formData.message.length,
        charCodes: Array.from(formData.message).map(c => c.charCodeAt(0)),
        bytes: new TextEncoder().encode(formData.message)
      });

      const response = await axios.post(`${API_BASE_URL}/api/ims/send-tts`, requestPayload, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        }
      });

      console.log('[FRONTEND] TTS Response:', response.data);

      if (response.data.success) {
        // 성공 시 크레딧 업데이트
        if (response.data.data?.remainingBalance !== undefined) {
          updateCreditBalance(response.data.data.remainingBalance);
        }

        setCallResults(prev => prev.map(r => ({ ...r, status: 'success' })));
        alert(`발신 성공!\n발신 건수: ${phones.length}건\n사용 크레딧: ${totalCost.toLocaleString()}원`);
      } else {
        setCallResults(prev => prev.map(r => ({ ...r, status: 'failed' })));
        alert(`발신 실패: ${response.data.message}`);

        // 크레딧 갱신 (실패 시에도 잔액 확인)
        await refreshCreditBalance();
      }
    } catch (error: any) {
      console.error('TTS send error:', error);
      setCallResults(prev => prev.map(r => ({ ...r, status: 'failed' })));
      alert(`발신 오류: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);

      // 크레딧 갱신
      await refreshCreditBalance();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getPhoneCount = () => {
    if (!formData.phoneNumbers) return 0;
    return formData.phoneNumbers.split('|').filter(p => p.trim()).length;
  };

  const getTotalCost = () => {
    return getPhoneCount() * COST_PER_CALL;
  };

  const loadFullCallHistory = async () => {
    setLoadingHistory(true);
    try {
      const hostname = window.location.hostname;
      const API_BASE_URL = process.env.REACT_APP_API_URL ||
        (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

      // 필터 파라미터 구성
      const params = new URLSearchParams();
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
      // TTS 전화 페이지에서는 TTS_MSG만 표시
      params.append('serviceType', 'TTS_MSG');

      const response = await axios.get(`${API_BASE_URL}/api/call-history?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data && Array.isArray(response.data)) {
        // 시간 역정렬 (최신순)
        const sortedData = response.data.sort((a: FullCallHistory, b: FullCallHistory) =>
          new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
        );
        setFullCallHistory(sortedData);
      }
    } catch (error: any) {
      console.error('Call history load error:', error);
      alert(`발신 내역 조회 오류: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
    } finally {
      setLoadingHistory(false);
    }
  };

  const downloadCSV = () => {
    const hostname = window.location.hostname;
    const API_BASE_URL = process.env.REACT_APP_API_URL ||
      (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

    // 필터 파라미터 구성
    const params = new URLSearchParams();
    if (filterStartDate) params.append('startDate', filterStartDate);
    if (filterEndDate) params.append('endDate', filterEndDate);
    if (filterStatus) params.append('status', filterStatus);
    if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
    // TTS 전화 페이지에서는 TTS_MSG만 다운로드
    params.append('serviceType', 'TTS_MSG');

    // CSV 다운로드 URL
    const url = `${API_BASE_URL}/api/call-history/export-csv?${params.toString()}`;
    const token = localStorage.getItem('token');

    // 새 창에서 다운로드
    const link = document.createElement('a');
    link.href = url;
    link.download = `call_history_${new Date().toISOString().slice(0, 10)}.csv`;

    // Authorization 헤더를 포함한 fetch 요청
    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `call_history_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('CSV download error:', error);
        alert('CSV 다운로드 중 오류가 발생했습니다.');
      });
  };

  const openFullHistoryModal = () => {
    setShowFullHistoryModal(true);
    loadFullCallHistory();
  };

  const applyFilters = () => {
    loadFullCallHistory();
  };

  const resetFilters = () => {
    setFilterStatus('');
    setFilterServiceType('');
    setFilterPhoneNumber('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  return (
    <div className="tts-call-page">
      <section className="tts-call-content section">
        <div className="container-narrow">
          <div className="tts-call-card card">
            <h2 className="card-title">TTS 전화 발신</h2>

            <div className="tts-call-layout">
              {/* 메인 폼 */}
              <div className="tts-call-main">
                <div className="card">
                  <h3 className="card-title">발신 정보</h3>

                  <div className="form-group">
                    <label className="form-label" htmlFor="phoneNumbers">
                      전화번호 *
                      <span className="form-hint">
                        (구분자: | 예: 010-1111-1111|010-2222-2222)
                      </span>
                    </label>
                    <div className="phone-input-group">
                      <textarea
                        id="phoneNumbers"
                        name="phoneNumbers"
                        className="form-textarea phone-textarea"
                        placeholder="010-1234-5678, 010-2345-6789 또는 | 구분 (쉼표, 파이프 모두 가능)"
                        value={formData.phoneNumbers}
                        onChange={handleChange}
                        rows={4}
                        required
                      />
                      <div className="phone-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setShowHistoryModal(true)}
                        >
                          이전 내역에서 선택
                        </button>
                        <label className="btn btn-secondary btn-sm file-upload-btn">
                          파일 업로드 (CSV/Excel)
                          <input
                            type="file"
                            accept=".csv,.txt,.xlsx,.xls"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                      <div className="phone-count">
                        총 {getPhoneCount()}개의 번호
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="message">
                      메시지 * <span style={{ fontSize: '0.875rem', color: formData.message.length > 60 ? '#ef4444' : '#6b7280' }}>({formData.message.length}/60자)</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      className="form-textarea"
                      placeholder="전송할 음성 메시지를 입력하세요 (최대 60자)"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      maxLength={60}
                      required
                    />
                    {messageTemplates.length > 0 && (
                      <div className="message-templates">
                        <p className="templates-label">최근 메시지:</p>
                        <div className="templates-list">
                          {messageTemplates.map(template => (
                            <div key={template.id} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <button
                                type="button"
                                className="template-item"
                                onClick={() => handleTemplateSelect(template)}
                                title={template.message}
                                style={{ flex: 1 }}
                              >
                                {template.message.substring(0, 30)}
                                {template.message.length > 30 ? '...' : ''}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const updated = messageTemplates.filter(t => t.id !== template.id);
                                  setMessageTemplates(updated);
                                  localStorage.setItem('messageTemplates', JSON.stringify(updated));
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '0.75rem',
                                  color: '#6b7280',
                                  backgroundColor: 'transparent',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#ef4444';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.borderColor = '#ef4444';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#6b7280';
                                  e.currentTarget.style.borderColor = '#d1d5db';
                                }}
                                title="이 메시지 삭제"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      onClick={handleSubmit}
                      className="btn btn-primary btn-lg"
                      disabled={getPhoneCount() === 0 || (user?.creditBalance || 0) < getTotalCost()}
                    >
                      전화 발신하기 ({getPhoneCount()}건 / {getTotalCost().toLocaleString()}원)
                    </button>
                  </div>
                </div>
              </div>

              {/* 사이드바 */}
              <div className="tts-call-sidebar">
                {/* 전체 발신 내역 버튼 */}
                <button
                  type="button"
                  onClick={openFullHistoryModal}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    marginBottom: '1rem',
                    fontSize: '0.9rem',
                    fontWeight: '500',
                    color: '#374151',
                    backgroundColor: '#f9fafb',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>📋</span>
                  <span>전체 발신 내역</span>
                </button>

                <div className="card">
                  <h3 className="card-title">실시간 발송 현황</h3>
                  <div className="call-results">
                    {callResults.length === 0 ? (
                      <div className="empty-state">
                        <p>발신을 시작하면 여기에 결과가 표시됩니다</p>
                      </div>
                    ) : (
                      <>
                        <div className="results-list">
                          {callResults.slice(0, 5).map(result => (
                            <div key={result.id} className={`result-item status-${result.status}`}>
                              <div className="result-phone">{result.phoneNumber}</div>
                              <div className="result-status">
                                {result.status === 'pending' && '대기중'}
                                {result.status === 'calling' && '발신중...'}
                                {result.status === 'success' && '✓ 성공'}
                                {result.status === 'failed' && '✗ 실패'}
                              </div>
                            </div>
                          ))}
                        </div>
                        {callResults.length > 5 && (
                          <div style={{ textAlign: 'center', padding: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>
                            외 {callResults.length - 5}건
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* 크레딧 및 비용 정보 */}
                <div className="card" style={{ marginTop: '1.5rem' }}>
                  <h3 className="card-title">크레딧 및 비용 정보</h3>
                  <div className="credit-info-box">
                    <div className="credit-info-item">
                      <span className="info-label">보유 크레딧</span>
                      <span className="info-value">{(user?.creditBalance || 0).toLocaleString()}원</span>
                    </div>
                    <div className="credit-info-item">
                      <span className="info-label">발신 건수</span>
                      <span className="info-value highlight">{getPhoneCount()}건</span>
                    </div>
                    <div className="credit-info-item">
                      <span className="info-label">예상 비용</span>
                      <span className="info-value cost">{getTotalCost().toLocaleString()}원</span>
                    </div>
                    <div className="credit-info-item">
                      <span className="info-label">발신 후 잔액</span>
                      <span className={`info-value ${(user?.creditBalance || 0) - getTotalCost() < 0 ? 'warning' : ''}`}>
                        {Math.max(0, (user?.creditBalance || 0) - getTotalCost()).toLocaleString()}원
                      </span>
                    </div>
                  </div>
                  <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
                    ※ 크레딧은 발신 성공 시에만 차감되며, 발신 실패 시에는 차감되지 않습니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 컬럼 선택 모달 */}
      {showColumnModal && (
        <div className="modal-overlay" onClick={() => setShowColumnModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>전화번호 컬럼 선택</h2>
              <button
                className="modal-close"
                onClick={() => setShowColumnModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                파일에서 전화번호가 포함된 컬럼을 선택해주세요.
              </p>
              <div className="column-selector">
                {fileHeaders.map((header, index) => (
                  <div key={index} className="column-option">
                    <label className="column-label">
                      <input
                        type="radio"
                        name="column"
                        value={index}
                        checked={selectedColumn === index}
                        onChange={() => setSelectedColumn(index)}
                      />
                      <div className="column-info">
                        <div className="column-name">{header || `컬럼 ${index + 1}`}</div>
                        <div className="column-preview">
                          미리보기: {fileRows.slice(0, 3).map(row => row[index]).join(', ')}
                          {fileRows.length > 3 && '...'}
                        </div>
                        <div className="column-count">
                          {fileRows.filter(row => row[index] && row[index].trim()).length}개 항목
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowColumnModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleColumnSelect}
                disabled={selectedColumn === -1}
              >
                선택 완료
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이전 내역 모달 */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>이전 발신 내역</h2>
              <button
                className="modal-close"
                onClick={() => setShowHistoryModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {callHistory.length === 0 ? (
                <div className="empty-state">
                  <p>저장된 발신 내역이 없습니다</p>
                </div>
              ) : (
                <div className="history-list">
                  {callHistory.map(history => (
                    <div key={history.id} className="history-item">
                      <div className="history-info">
                        <div className="history-date">
                          {new Date(history.timestamp).toLocaleString('ko-KR')}
                        </div>
                        <div className="history-count">
                          {history.count}개 번호
                        </div>
                      </div>
                      <div className="history-phones">
                        {history.phoneNumbers.split('|').slice(0, 3).join(', ')}
                        {history.count > 3 && ` 외 ${history.count - 3}개`}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleHistorySelect(history)}
                        >
                          {history.phoneNumbers.split('|').every(p => selectedHistory.includes(p))
                            ? '선택 해제'
                            : '선택'}
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => {
                            const updated = callHistory.filter(h => h.id !== history.id);
                            setCallHistory(updated);
                            localStorage.setItem('callHistory', JSON.stringify(updated));
                          }}
                          style={{
                            padding: '4px 12px',
                            fontSize: '0.875rem',
                            color: '#ef4444',
                            backgroundColor: 'transparent',
                            border: '1px solid #ef4444',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <div className="selected-count">
                선택된 번호: {selectedHistory.length}개
              </div>
              <button
                className="btn btn-primary"
                onClick={addSelectedToForm}
                disabled={selectedHistory.length === 0}
              >
                선택한 번호 추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 전체 발신 내역 모달 */}
      {showFullHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowFullHistoryModal(false)}>
          <div className="modal-content" style={{ maxWidth: '1100px', width: '95%' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>전체 발신 내역 (응답 결과 기준)</h2>
              <button
                className="modal-close"
                onClick={() => setShowFullHistoryModal(false)}
              >
                ✕
              </button>
            </div>

            {/* 필터 섹션 */}
            <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>시작일</label>
                  <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>종료일</label>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>전화번호</label>
                  <input
                    type="text"
                    value={filterPhoneNumber}
                    onChange={(e) => setFilterPhoneNumber(e.target.value)}
                    placeholder="전화번호 검색"
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>상태</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value="">전체</option>
                    <option value="success">성공</option>
                    <option value="failed">실패</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>유형</label>
                  <select
                    value={filterServiceType}
                    onChange={(e) => setFilterServiceType(e.target.value)}
                    style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                  >
                    <option value="">전체</option>
                    <option value="TTS_MSG">일반</option>
                    <option value="TTS_RS">설문</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                  <button
                    onClick={applyFilters}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    필터 적용
                  </button>
                  <button
                    onClick={resetFilters}
                    style={{
                      flex: 1,
                      padding: '8px 16px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    초기화
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={downloadCSV}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <span>📥</span>
                  <span>CSV 다운로드</span>
                </button>
              </div>
            </div>

            <div className="modal-body">
              {loadingHistory ? (
                <div className="empty-state">
                  <p>발신 내역을 불러오는 중...</p>
                </div>
              ) : fullCallHistory.length === 0 ? (
                <div className="empty-state">
                  <p>발신 내역이 없습니다</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                        <th style={{ padding: '12px 8px', textAlign: 'left' }}>발신일시</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center' }}>유형</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left' }}>전화번호</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left' }}>메시지</th>
                        <th style={{ padding: '12px 8px', textAlign: 'center' }}>상태</th>
                        <th style={{ padding: '12px 8px', textAlign: 'left' }}>설문응답</th>
                        <th style={{ padding: '12px 8px', textAlign: 'right' }}>비용</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fullCallHistory.map((item) => (
                        <tr key={item.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <td style={{ padding: '12px 8px' }}>
                            {new Date(item.sentAt).toLocaleString('ko-KR', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{
                              padding: '3px 8px',
                              borderRadius: '8px',
                              fontSize: '0.8rem',
                              fontWeight: '500',
                              backgroundColor: item.serviceType === 'TTS_RS' ? '#dbeafe' : '#e0e7ff',
                              color: item.serviceType === 'TTS_RS' ? '#1e40af' : '#4338ca'
                            }}>
                              {item.serviceType === 'TTS_RS' ? '설문' : '일반'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px' }}>{item.phoneNumber}</td>
                          <td style={{ padding: '12px 8px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.message}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: '500',
                              backgroundColor: item.status === 'success' ? '#d1fae5' : item.status === 'failed' ? '#fee2e2' : '#fef3c7',
                              color: item.status === 'success' ? '#065f46' : item.status === 'failed' ? '#991b1b' : '#92400e'
                            }}>
                              {item.status === 'success' && '✓ 성공'}
                              {item.status === 'failed' && '✗ 실패'}
                              {item.status === 'pending' && '대기중'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', maxWidth: '200px' }}>
                            {item.surveyAnswers && item.surveyAnswers.length > 0 ? (
                              <div style={{ fontSize: '0.85rem' }}>
                                {item.surveyAnswers.map((answer, idx) => (
                                  <div key={idx} style={{ marginBottom: '4px' }}>
                                    <span style={{ fontWeight: '500', color: '#1e40af' }}>답변: {answer.answer}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>-</span>
                            )}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                            {item.cost.toLocaleString()}원
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ marginTop: '1rem', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span>총 발신 건수: <strong>{fullCallHistory.length}건</strong></span>
                      <span>성공: <strong style={{ color: '#065f46' }}>{fullCallHistory.filter(h => h.status === 'success').length}건</strong></span>
                      <span>실패: <strong style={{ color: '#991b1b' }}>{fullCallHistory.filter(h => h.status === 'failed').length}건</strong></span>
                      <span>총 비용: <strong>{fullCallHistory.reduce((sum, h) => sum + h.cost, 0).toLocaleString()}원</strong></span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowFullHistoryModal(false)}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TtsCall;
